import {
	ConnectCalendarPostRequest,
	DickerDataCreateOrUpdateRequest,
	EmailIntegration,
	IntegrationsResponse,
	UnsubscribeEmailsInterface
} from "@/Contracts/Settings";
import axios, { AxiosResponse } from "axios";

import {
	deleteRequest,
	getRequest,
	postRequest,
	putRequest
} from "@/Utils/Service/BackendService";

import { DeletedRecord } from "@/Contracts";
import {
	allZeroesGuid,
	ChildEntityHydrationParams,
	getTableQueryFilters,
	includeChildEntities
} from "@/Contracts/Utils";
import {
	AccessAndRefreshTokenResponse,
	AddCompanyUserRequest,
	AddCustomDomainContract,
	AddTaxCategoryRequest,
	AddTaxRegionRequest,
	ChargebeePortalSession,
	BillingInfo,
	ChangeCompanyLogoRequest,
	ChargebeeCreditNoteDetail,
	ChargebeeCreditNoteResponse,
	ChargebeeInvoice,
	ChargebeeInvoiceResponse,
	CompanyUsersRequest,
	CompanyUsersResponse,
	ConfigurationSettingsResponse,
	CreateLibraryItemRequest,
	CustomDomainItem,
	GetDatePatternsResponse,
	EmailAliasItem,
	EmailAliasRequest,
	GetLibraryRequest,
	GetLibraryResponse,
	GetUserCompanyRequest,
	LibraryItem,
	PauseChargebeeSubscriptionRequest,
	PricingPlanEstimateData,
	PricingPlanEstimateRequest,
	PricingPlanResponse,
	PricingPlanSummaryResponse,
	PricingPlanUserRequest,
	TaxCategoriesResponse,
	TaxCategory,
	TaxRegion,
	TaxRegionsResponse,
	UpdatePricingPlanRequest,
	UserCalendarResponse,
	UserCompany,
	UserData,
	UserEmailSignature,
	HubspotUserTokenResponse,
	CompanyPublicDataResponse,
	GetConfigurationRequest,
	UpdateBillingInfoRequest
} from "@/Contracts/Users";
import { UserCompanyConfigUpdateRequest } from "@/Contracts/Login";
import { PreferenceSettings } from "@/Contracts";
import { PendingUser, PendingUserResponse } from "@/Contracts/Users";

const getUserDataUrl = `/v2/users/${allZeroesGuid}`;
const editUserDataUrl = "/v2/users/settings/{user_id} ";
const editCompanyAuthUrl = "/v2/companies/auth";
const getUserConfigurationUrl = "/v2/users/config";
const userCompanyConfigUpdateUrl = "/v2/users/config";
const editUserPasswordUrl = "/v2/users/password/reset";
const getTaxCategoriesListUrl = "/v2/tax/categories";
const addTaxCategoryUrl = "/v2/tax/categories";
const editTaxCategoryUrl = "/v2/tax/categories/{taxCategoryId}";
const getTaxRegionsListUrl = "/v2/tax/regions";
const addTaxRegionUrl = "/v2/tax/regions";
const editTaxRegionUrl = "/v2/tax/regions/{taxRegionId}";
const getUserCompanyUrl = `/v2/companies`;
const editUserCompanyUrl = "/v2/companies";
const getDatePatternsUrl = "/v2/date_patterns";
const getCompanyUsersUrl = "/v2/users?company_id={company_id}";
const deleteCompanyUserUrl = "/v2/users/{user_id}";
const addCompanyUserUrl = "/v2/users";
const addApiUserUrl = "/v2/users/api_user";
const fetchAcessAndRefreshTokensUrl =
	"/v2/users/access_token/{user_id}?regenerate_access_token={regenerate_token}";
const editUserPreferenceUrl = "/v2/preferences/{user_id}";
const changeCompanyLogoUrl = "/v2/companies";
const fetchNylasAuthLinkURL = "/v2/emails/new";
const addEmailAliasUrl = "/v2/emails/alias/";
const deleteEmailAliasUrl = "/v2/emails/alias/delete";
const verifyEmail = "/v2/verify/email";
const deleteCustomEmail = "/v2/verify/email/delete";
const customDomain = "/v2/verify/domain/dkim/";
const domainVerificationRetryUrl = "/v2/verify/domain/dkim/retry";
const deleteCustomDomain = "/v2/verify/domain/dkim/delete";
const resendDomainVerification = "/v2/verify/domain/dkim/email/";
const disableUserUrl = "/v2/users/disable/{user_id}";
const enableUserUrl = "/v2/users/disable/undo/{user_id}";
const editCompanyPreferencesUrl = "/v2/companies/preferences/{company_id}";
const updateTranslateLanguageUrl = "/v2/companies/language";
const getPendingUsersURL = "/v2/user/requests";
const getPendingUserURL = "/v2/user/requests/:userRequestId";
const acceptPendingUserURL = "/v2/user/requests/:userRequestId/accept";
const rejectPendingUserURL = "/v2/user/requests/:userRequestId/reject";
const acceptVendorPendingUserURL =
	"/v2/user/requests/:userRequestId/accept-vendor";
const getUserSimilarCompanyUrl = "/v2/signup/companies/similar";
const userRequestJoinCompanyUrl =
	"/v2/signup/company/:companyId/request-to-join";
const userWithdrawJoinCompanyUrl =
	"/v2/signup/company/:companyId/withdraw-request";
// library
const getLibraryDataUrl = "/v2/library";
const createLibraryItemUrl = "/v2/library/";
const deleteLibraryItemUrl = "/v2/library/{library_item_id}";

// payments
const confirmCardAddedUrl = "/v2/companies/payments/confirm_card_add";
const getChargebeePortalSessionUrl = "v2/companies/chargebee_portal";
const getBillingInfoUrl = "/v2/companies/billing_info";
const updateBillingInfoUrl = "/v2/companies/billing_info";
const getPricingPlansURL = "/v2/companies/plan/details";
const getPlanSummaryURL = "/v2/companies/subscription/details";
const planEstimateURL = "v2/companies/subscription/estimates";
const updatePricingPlanURL = "/v2/companies/subscription/change/plan";
const addUserURL = "/v2/companies/subscription/add/users";
const removeUserURL = "/v2/companies/subscription/remove/users";

const fetchUserCalendarURL = "/v2/calendars";
const connectUserCalendarURL = "/v2/calendars/settings";

const getCompanyIntegrationsUrl = "/v2/companies/integrations";

const getInvoiceListUrl = "/v2/companies/billing_info/invoices";
const getInvoiceDetailsUrl = "/v2/companies/billing_info/invoice/{invoice_id}";
const getCreditNoteListUrl = "/v2/companies/billing/info/credit-notes";
const getCreditNoteDetailsUrl =
	"/v2/companies/billing/info/credit-notes/{credit_note_id}";
const pauseSubscriptionUrl = "/v2/companies/billing_info/cancel/request";

const updateEmailPreferencesUrl = "/v2/email/settings";

const unsubscribeEmailSettingsUrl = "/v2/unsubscribe-emails";

const updateUserSignatureURL = "/v2/email/signatures";
const fetchUserSignatureURL = "/v2/email/signatures";

const onboardingScheduledURL = "/v2/companies/onboarding/scheduled/";
const addDickerDataIntegrationURL = "/v2/companies/integrations/dickerdata/";
const deleteDickerDataIntegrationURL =
	"/v2/companies/integrations/dickerdata/delete";
const connectDatekDistributorURL = "/v2/companies/integrations/datek/connect/";

const getHubspotUserTokenUrl = "/v2/chat/hubspot/token";

const resetUserMFAUrl = "/v2/users/reset/mfa/{id}";

const resetPasswordUrl = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/dbconnections/change_password`;

const capturePaymentLeadUrl = "v2/users/capture-z-pay-lead";

const getSurveyDataUrl = "/v2/survey/{survey_name}/response";
const putSurveyDataUrl = "/v2/survey/{survey_name}/response";

export const getCompanyIntegrations =
	async (): Promise<IntegrationsResponse> => {
		try {
			const response: AxiosResponse<IntegrationsResponse> = await axios(
				getRequest(getCompanyIntegrationsUrl, null, true, false, false)
			);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

export const getUserData = async (): Promise<UserData> => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			getRequest(getUserDataUrl, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editUserData = async (userData: UserData): Promise<UserData> => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			putRequest(
				editUserDataUrl.replace("{user_id}", userData.id),
				userData,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editCompanyPreferences = async (
	id: string,
	preferences: PreferenceSettings
): Promise<UserCompany> => {
	try {
		const url = editCompanyPreferencesUrl.replace("{company_id}", id);
		const response = await axios(
			putRequest(url, preferences, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const setTwoFactorAuth = async (
	is2fa_enabled: boolean
): Promise<UserCompany> => {
	try {
		const response: AxiosResponse<UserCompany> = await axios(
			putRequest(
				editCompanyAuthUrl,
				{ is2fa_enabled },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const setSsoMandatory = async (
	isSsoMandatory: boolean
): Promise<UserCompany> => {
	try {
		const response: AxiosResponse<UserCompany> = await axios(
			putRequest(
				editCompanyAuthUrl,
				{ is_sso_mandatory: isSsoMandatory },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const setSsoEnable = async (
	isSsoEnabled: boolean
): Promise<UserCompany> => {
	try {
		const response: AxiosResponse<UserCompany> = await axios(
			putRequest(
				editCompanyAuthUrl,
				{ is_sso_enabled: isSsoEnabled },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getConfigurationSettings = async (
	getConfigurationRequest?: GetConfigurationRequest
): Promise<ConfigurationSettingsResponse> => {
	try {
		let url = getUserConfigurationUrl;
		if (getConfigurationRequest) {
			const included_child_entities = includeChildEntities(
				getConfigurationRequest.included_child_entities
			);
			url = url + `?${included_child_entities}`;
		}
		const response: AxiosResponse<ConfigurationSettingsResponse> =
			await axios(getRequest(url, null, true, false, false));
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editUserPassword = async (password: string): Promise<UserData> => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			putRequest(editUserPasswordUrl, { password }, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getUserCompany = async (
	getUserCompanyRequest: GetUserCompanyRequest | null
): Promise<UserCompany> => {
	try {
		const includedChildEntities = includeChildEntities(
			getUserCompanyRequest?.included_child_entities
		);
		const url =
			getUserCompanyUrl +
			(includedChildEntities && includedChildEntities.length > 0
				? "?" + includedChildEntities
				: "");
		const response: AxiosResponse<UserCompany> = await axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editUserCompany = async (
	userCompany: UserCompany
): Promise<UserCompany> => {
	try {
		const response: AxiosResponse<UserCompany> = await axios(
			putRequest(editUserCompanyUrl, userCompany, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getDatePatterns = async () => {
	try {
		const response: AxiosResponse<GetDatePatternsResponse> = await axios(
			getRequest(getDatePatternsUrl, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getCompanyUsers = async (
	companyId: string,
	companyUsersRequest: CompanyUsersRequest
): Promise<CompanyUsersResponse> => {
	try {
		let url = getCompanyUsersUrl;
		if (companyUsersRequest.filters.length > 0) {
			const appliedFilters = getTableQueryFilters(
				companyUsersRequest.filters,
				false
			);
			url =
				url +
				"?" +
				appliedFilters +
				(companyUsersRequest.keywords &&
				companyUsersRequest.keywords.length > 0
					? `&q=${companyUsersRequest.keywords}`
					: "");
		} else {
			url =
				url +
				(companyUsersRequest.keywords &&
				companyUsersRequest.keywords.length > 0
					? `?q=${companyUsersRequest.keywords}`
					: "");
		}
		const response: AxiosResponse<CompanyUsersResponse> = await axios(
			getRequest(
				url.replace("{company_id}", companyId),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteCompanyUser = async (
	companyUser: UserData
): Promise<DeletedRecord> => {
	try {
		const response: AxiosResponse<DeletedRecord> = await axios(
			deleteRequest(
				deleteCompanyUserUrl.replace("{user_id}", companyUser.id),
				null,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addCompanyUser = async (
	addCompanyUserRequest: AddCompanyUserRequest
): Promise<UserData> => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			postRequest(
				addCompanyUserUrl,
				addCompanyUserRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addApiUser = async (addApiUserRequest: any): Promise<UserData> => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			postRequest(addApiUserUrl, addApiUserRequest, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchAccessAndRefreshTokens = async (
	userId: string,
	regenerateToken = false
): Promise<AccessAndRefreshTokenResponse> => {
	try {
		const response = await axios(
			getRequest(
				fetchAcessAndRefreshTokensUrl
					.replace("{user_id}", userId)
					.replace(
						"{regenerate_token}",
						regenerateToken ? "true" : "false"
					),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const verifyUserEmailService = async (emailId: string, name: string) => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			postRequest(
				verifyEmail,
				{ email: emailId, name: name },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteCustomEmailService = async (emailId: string) => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			putRequest(
				deleteCustomEmail,
				{ email: emailId },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addCustomDomainService = async (
	addCustomDomain: AddCustomDomainContract
) => {
	try {
		const response: AxiosResponse<CustomDomainItem> = await axios(
			postRequest(customDomain, addCustomDomain, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const retryDomainVerification = async (
	domain: string
): Promise<UserCompany> => {
	try {
		const url = domainVerificationRetryUrl + `?domain=${domain}`;
		const response: AxiosResponse<UserCompany> = await axios(
			putRequest(url, {}, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const resendDomainVerificationEmail = async (
	emailId: string
): Promise<CustomDomainItem> => {
	try {
		const response: AxiosResponse<CustomDomainItem> = await axios(
			getRequest(
				resendDomainVerification + emailId,
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteCustomDomainService = async (
	deleteCustomDomainObject: AddCustomDomainContract
) => {
	try {
		const response: AxiosResponse<CustomDomainItem> = await axios(
			putRequest(
				deleteCustomDomain,
				deleteCustomDomainObject,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editCompanyUser = async (
	companyUser: UserData
): Promise<UserData> => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			putRequest(
				editUserPreferenceUrl.replace("{user_id}", companyUser.id),
				companyUser,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getTaxCategoriesList =
	async (): Promise<TaxCategoriesResponse> => {
		try {
			const response: AxiosResponse<TaxCategoriesResponse> = await axios(
				getRequest(getTaxCategoriesListUrl, null, true, false, false)
			);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

export const getTaxRegionsList = async (): Promise<TaxRegionsResponse> => {
	try {
		const response: AxiosResponse<TaxRegionsResponse> = await axios(
			getRequest(getTaxRegionsListUrl, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addTaxRegion = async (
	addTaxRegionRequest: AddTaxRegionRequest
): Promise<TaxRegion> => {
	try {
		const response: AxiosResponse<TaxRegion> = await axios(
			postRequest(
				addTaxRegionUrl,
				addTaxRegionRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editTaxRegion = async (
	editTaxRegionRequest: TaxRegion
): Promise<TaxRegion> => {
	try {
		const response: AxiosResponse<TaxRegion> = await axios(
			putRequest(
				editTaxRegionUrl.replace(
					"{taxRegionId}",
					editTaxRegionRequest.id
				),
				editTaxRegionRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addTaxCategory = async (
	addTaxCategoryRequest: AddTaxCategoryRequest
): Promise<TaxCategory> => {
	try {
		const response: AxiosResponse<TaxCategory> = await axios(
			postRequest(
				addTaxCategoryUrl,
				addTaxCategoryRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editTaxCategory = async (
	editTaxCategoryRequest: TaxCategory
): Promise<TaxCategory> => {
	try {
		const response: AxiosResponse<TaxCategory> = await axios(
			putRequest(
				editTaxCategoryUrl.replace(
					"{taxCategoryId}",
					editTaxCategoryRequest.id
				),
				editTaxCategoryRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const changeCompanyLogo = async (
	changeCompanyLogoRequest: ChangeCompanyLogoRequest
): Promise<UserCompany> => {
	try {
		const response: AxiosResponse<UserCompany> = await axios(
			putRequest(
				changeCompanyLogoUrl,
				changeCompanyLogoRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateTranslateLanguage = async (
	language: string
): Promise<UserCompany> => {
	try {
		const response: AxiosResponse<UserCompany> = await axios(
			putRequest(
				updateTranslateLanguageUrl,
				{
					language
				},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getLibraryData = async (
	fetchLibraryRequest: GetLibraryRequest
): Promise<GetLibraryResponse> => {
	try {
		let queryParams = "";
		if (
			fetchLibraryRequest.filters &&
			fetchLibraryRequest.filters.length > 0
		) {
			const filterUrlQuery = getTableQueryFilters(
				fetchLibraryRequest.filters,
				false
			);
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				filterUrlQuery && filterUrlQuery.length > 0
					? `${filterUrlQuery}`
					: ""
			}`;
		}

		if (
			fetchLibraryRequest.included_child_entities &&
			fetchLibraryRequest.included_child_entities.length > 0
		) {
			const childEntityUrlQuery = includeChildEntities(
				fetchLibraryRequest.included_child_entities
			);
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				childEntityUrlQuery && childEntityUrlQuery.length > 0
					? `${childEntityUrlQuery}`
					: ""
			}`;
		}

		const response: AxiosResponse<GetLibraryResponse> = await axios(
			getRequest(
				`${getLibraryDataUrl}${
					queryParams.length > 0 ? `?${queryParams}` : ""
				}`,
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const createLibraryResource = async (
	createLibraryItemRequest: CreateLibraryItemRequest
): Promise<LibraryItem> => {
	try {
		const response: AxiosResponse<LibraryItem> = await axios(
			postRequest(
				createLibraryItemUrl,
				createLibraryItemRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateLibraryResourceTags = async (
	libraryItemId: string,
	tagIds: string[]
): Promise<LibraryItem> => {
	try {
		const response: AxiosResponse<LibraryItem> = await axios(
			putRequest(
				`/v2/library/${libraryItemId}/`,
				{ tag_ids: tagIds },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteLibraryResource = async ({
	libraryItemId
}: {
	libraryItemId: string;
}): Promise<DeletedRecord> => {
	try {
		const response: AxiosResponse<DeletedRecord> = await axios(
			deleteRequest(
				deleteLibraryItemUrl.replace(
					"{library_item_id}",
					libraryItemId
				),
				null,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const confirmCardAdded = async (): Promise<BillingInfo> => {
	try {
		const response: AxiosResponse<BillingInfo> = await axios(
			getRequest(confirmCardAddedUrl, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getChargebeePortalSession =
	async (): Promise<ChargebeePortalSession> => {
		try {
			const response: AxiosResponse<ChargebeePortalSession> = await axios(
				getRequest(
					getChargebeePortalSessionUrl,
					null,
					true,
					false,
					false
				)
			);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

export const getBillingInfo = async (): Promise<BillingInfo> => {
	try {
		const response: AxiosResponse<BillingInfo> = await axios(
			getRequest(getBillingInfoUrl, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateBillingInfo = async (
	billingInfo: UpdateBillingInfoRequest
): Promise<BillingInfo> => {
	try {
		const response: AxiosResponse<BillingInfo> = await axios(
			putRequest(updateBillingInfoUrl, billingInfo, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getPricingPlans = async (): Promise<PricingPlanResponse> => {
	try {
		const response = await axios(
			getRequest(getPricingPlansURL, null, true, false, false)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const getPricingPlanSummary =
	async (): Promise<PricingPlanSummaryResponse> => {
		try {
			const response = await axios(
				getRequest(getPlanSummaryURL, null, true, false, false)
			);
			return response.data;
		} catch (err) {
			return Promise.reject(err);
		}
	};

export const updatePricingPlan = async (
	requestBody: UpdatePricingPlanRequest
): Promise<PricingPlanSummaryResponse> => {
	try {
		const response = await axios(
			putRequest(updatePricingPlanURL, requestBody, true, false, false)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const planEstimate = async (
	requestBody: PricingPlanEstimateRequest
): Promise<PricingPlanEstimateData> => {
	try {
		const response = await axios(
			postRequest(planEstimateURL, requestBody, true, false, false)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const addUserPricingPlan = async (
	requestBody: PricingPlanUserRequest
): Promise<PricingPlanSummaryResponse> => {
	try {
		const response = await axios(
			putRequest(addUserURL, requestBody, true, false, false)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const removeUserPricingPlan = async (
	requestBody: PricingPlanUserRequest
): Promise<PricingPlanSummaryResponse> => {
	try {
		const response = await axios(
			putRequest(removeUserURL, requestBody, true, false, false)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const addEmailAlias = async (
	addEmailAliasRequest: EmailAliasRequest
): Promise<EmailAliasItem> => {
	try {
		const response: AxiosResponse<EmailAliasItem> = await axios(
			putRequest(
				addEmailAliasUrl,
				addEmailAliasRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteEmailAlias = async (
	deleteEmailAliasRequest: EmailAliasRequest
): Promise<EmailAliasItem> => {
	try {
		const response: AxiosResponse<EmailAliasItem> = await axios(
			putRequest(
				deleteEmailAliasUrl,
				deleteEmailAliasRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const disableUser = async (userId: string): Promise<UserData> => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			putRequest(
				disableUserUrl.replace("{user_id}", userId),
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const enableUser = async (userId: string): Promise<UserData> => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			putRequest(
				enableUserUrl.replace("{user_id}", userId),
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const fetchUserCalendars = async (): Promise<UserCalendarResponse> => {
	try {
		const response: AxiosResponse<UserCalendarResponse> = await axios(
			getRequest(fetchUserCalendarURL, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const connectUserCalendar = async (
	connectCalendarPostRequest: ConnectCalendarPostRequest
) => {
	try {
		const response: AxiosResponse<any> = await axios(
			postRequest(
				connectUserCalendarURL,
				connectCalendarPostRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getChargebeeInvoiceList = async (
	pageOffset?: string[]
): Promise<ChargebeeInvoiceResponse> => {
	try {
		let url = getInvoiceListUrl;
		if (pageOffset) url += `?offset=${JSON.stringify(pageOffset)}`;
		const response: AxiosResponse<ChargebeeInvoiceResponse> = await axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getChargebeeInvoiceDetails = async (
	invoiceId: string
): Promise<ChargebeeInvoice> => {
	try {
		const response: AxiosResponse<ChargebeeInvoice> = await axios(
			getRequest(
				getInvoiceDetailsUrl.replace("{invoice_id}", invoiceId),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getChargebeeCreditNotesList = async (
	pageOffset?: string[]
): Promise<ChargebeeCreditNoteResponse> => {
	try {
		let url = getCreditNoteListUrl;
		if (pageOffset) url += `?offset=${JSON.stringify(pageOffset)}`;
		const response: AxiosResponse<ChargebeeCreditNoteResponse> =
			await axios(getRequest(url, null, true, false, false));
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getChargebeeCreditNoteDetails = async (
	creditNoteId: string
): Promise<ChargebeeCreditNoteDetail> => {
	try {
		const response: AxiosResponse<ChargebeeCreditNoteDetail> = await axios(
			getRequest(
				getCreditNoteDetailsUrl.replace(
					"{credit_note_id}",
					creditNoteId
				),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const pauseChargebeeSubscription = async (
	pauseChargebeeSubscriptionRequest: PauseChargebeeSubscriptionRequest
): Promise<void> => {
	try {
		const response: AxiosResponse<void> = await axios(
			putRequest(
				pauseSubscriptionUrl,
				pauseChargebeeSubscriptionRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateEmailSettings = async (
	userEmailIntegrationRequest: EmailIntegration
): Promise<EmailIntegration> => {
	try {
		const response: AxiosResponse<EmailIntegration> = await axios(
			putRequest(
				updateEmailPreferencesUrl,
				userEmailIntegrationRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const unsubscribeEmailSettings = async (
	unsubscribeEmailSettingsRequest: UnsubscribeEmailsInterface,
	userId: string
): Promise<UnsubscribeEmailsInterface> => {
	try {
		const encodedUserId = encodeURIComponent(userId);
		const url = `${unsubscribeEmailSettingsUrl}?id=${encodedUserId}`;
		const response: AxiosResponse<UnsubscribeEmailsInterface> = await axios(
			postRequest(
				url,
				unsubscribeEmailSettingsRequest,
				false,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateUserSignature = async (
	updatedUserSignature: UserEmailSignature
): Promise<UserEmailSignature> => {
	try {
		const response: AxiosResponse<UserEmailSignature> = await axios(
			postRequest(
				updateUserSignatureURL,
				updatedUserSignature,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchUserSignature = async (): Promise<UserEmailSignature> => {
	try {
		const response: AxiosResponse<UserEmailSignature> = await axios(
			getRequest(fetchUserSignatureURL, null, true, false, false)
		);

		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const onboardingCallScheduled = async (): Promise<unknown> => {
	try {
		const response: AxiosResponse<unknown> = await axios(
			putRequest(onboardingScheduledURL, {}, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addDickerDataIntegration = async (
	dickerDataRequest: DickerDataCreateOrUpdateRequest
): Promise<IntegrationsResponse> => {
	try {
		const response: AxiosResponse<IntegrationsResponse> = await axios(
			putRequest(
				addDickerDataIntegrationURL,
				dickerDataRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
export const deleteDickerDataIntegration =
	async (): Promise<IntegrationsResponse> => {
		try {
			const response: AxiosResponse<IntegrationsResponse> = await axios(
				putRequest(
					deleteDickerDataIntegrationURL,
					{},
					true,
					false,
					false
				)
			);

			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

export const connectDatekDistributor = async (
	supplierId: string
): Promise<IntegrationsResponse> => {
	try {
		const response: AxiosResponse<IntegrationsResponse> = await axios(
			postRequest(
				connectDatekDistributorURL,
				{ supplier_id: supplierId },
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const userCompanyConfigUpdate = async (
	userCompanyConfigUpdateRequest: UserCompanyConfigUpdateRequest
): Promise<ConfigurationSettingsResponse> => {
	try {
		const response: AxiosResponse<ConfigurationSettingsResponse> =
			await axios(
				postRequest(
					userCompanyConfigUpdateUrl,
					userCompanyConfigUpdateRequest,
					true,
					false,
					false
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getHubspotUserToken =
	async (): Promise<HubspotUserTokenResponse> => {
		try {
			const response: AxiosResponse<HubspotUserTokenResponse> =
				await axios(
					getRequest(getHubspotUserTokenUrl, null, true, false, false)
				);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

export const getPendingUsers = async (): Promise<PendingUserResponse> => {
	try {
		const includedChildEntities = includeChildEntities([
			ChildEntityHydrationParams.USERS_ALL,
			ChildEntityHydrationParams.VENDORS_ALL
		]);
		const response: AxiosResponse<PendingUserResponse> = await axios(
			getRequest(
				`${getPendingUsersURL}?${includedChildEntities}`,
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getUserSimilarCompany =
	async (): Promise<CompanyPublicDataResponse> => {
		try {
			const response: AxiosResponse<CompanyPublicDataResponse> =
				await axios(
					getRequest(
						getUserSimilarCompanyUrl,
						null,
						true,
						false,
						false
					)
				);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};
export const userRequestJoinCompany = async (
	companyId: string,
	joinCompanyRequest: GetUserCompanyRequest | null
): Promise<ConfigurationSettingsResponse> => {
	try {
		const includedChildEntities = includeChildEntities(
			joinCompanyRequest?.included_child_entities
		);
		let url = userRequestJoinCompanyUrl.replace(":companyId", companyId);
		url =
			url +
			(includedChildEntities && !!includedChildEntities.length
				? "?" + includedChildEntities
				: "");
		const response: AxiosResponse<ConfigurationSettingsResponse> =
			await axios(postRequest(url, {}, true, false, false));
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
export const getPendingUserDetails = async (
	pendingUserId: string,
	includedChildEntities: string
): Promise<PendingUser> => {
	try {
		const pendingUserURL =
			getPendingUserURL.replace(":userRequestId", pendingUserId) +
			`?${includedChildEntities}`;
		const response: AxiosResponse<PendingUser> = await axios(
			getRequest(pendingUserURL, null, true, false, false)
		);

		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
export const acceptPendingUser = async (
	userId: string,
	acceptPendingUserRequest: AddCompanyUserRequest
): Promise<UserData> => {
	try {
		const response: AxiosResponse<UserData> = await axios(
			postRequest(
				acceptPendingUserURL.replace(":userRequestId", userId),
				acceptPendingUserRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const rejectPendingUser = async (userId: string): Promise<void> => {
	try {
		const response: AxiosResponse<void> = await axios(
			postRequest(
				rejectPendingUserURL.replace(":userRequestId", userId),
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const acceptVendorPendingUser = async (
	userId: string,
	includedChildEntities: string
): Promise<PendingUser> => {
	try {
		const response: AxiosResponse<PendingUser> = await axios(
			postRequest(
				acceptVendorPendingUserURL.replace(":userRequestId", userId) +
					`?${includedChildEntities}`,
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
export const userRequestWithdrawCompany = async (
	companyId: string,
	withdrawCompanyRequest: GetUserCompanyRequest | null
): Promise<ConfigurationSettingsResponse> => {
	try {
		const includedChildEntities = includeChildEntities(
			withdrawCompanyRequest?.included_child_entities
		);
		let url = userWithdrawJoinCompanyUrl.replace(":companyId", companyId);
		url =
			url +
			(includedChildEntities && !!includedChildEntities.length
				? "?" + includedChildEntities
				: "");
		const response: AxiosResponse<ConfigurationSettingsResponse> =
			await axios(postRequest(url, {}, true, false, false));
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const resetUserMFA = async (userId: string): Promise<void> => {
	try {
		await axios(
			deleteRequest(
				resetUserMFAUrl.replace("{id}", userId),
				null,
				true,
				false
			)
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const resetPassword = async (email: string): Promise<void> => {
	try {
		await axios.post(
			resetPasswordUrl,
			{
				client_id: process.env.REACT_APP_AUTH0_CLIENT,
				email,
				connection: "Username-Password-Authentication"
			},
			{
				headers: {
					"Content-Type": "application/json"
				}
			}
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const createPaymentsLead = async (
	source: string,
	connect_now_clicked = false
): Promise<void> => {
	try {
		await axios(
			postRequest(
				capturePaymentLeadUrl,
				{
					source: source,
					zomentum_payment_connect_now_clicked: connect_now_clicked
				},
				true,
				false,
				false
			)
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getSurveyData = async (surveyName: string) => {
	try {
		const response = await axios(
			getRequest(
				getSurveyDataUrl.replace("{survey_name}", surveyName),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateSurveyData = async (surveyName: string, surveyData: any) => {
	try {
		const response = await axios(
			postRequest(
				putSurveyDataUrl.replace("{survey_name}", surveyName),
				surveyData,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchNylasAuthURL = async (): Promise<string> => {
	try {
		const response: AxiosResponse<string> = await axios(
			getRequest(fetchNylasAuthLinkURL, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
